import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import LocalizedLink from '../components/common/LocalizedLink';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/common/SEO';
import {
  FiCheckCircle,
  FiLayers,
  FiShield,
  FiClock,
  FiSmartphone,
  FiGlobe,
  FiBell,
  FiCalendar,
  FiLock,
  FiShare2,
  FiHeart,
  FiUsers,
  FiMapPin,
  FiStar,
  FiEdit,
  FiDatabase,
  FiZap,
  FiRefreshCw
} from 'react-icons/fi';

const FeaturesPage = () => {
  const { t } = useLanguage();

  const heroRef = useRef(null);
  const coreRef = useRef(null);
  const advancedRef = useRef(null);
  const comingSoonRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const coreInView = useInView(coreRef, { once: true, margin: "-100px" });
  const advancedInView = useInView(advancedRef, { once: true, margin: "-100px" });
  const comingSoonInView = useInView(comingSoonRef, { once: true, margin: "-100px" });

  // Structured Data for Features Page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": t('common.seo.features.title'),
    "description": t('common.seo.features.description'),
    "url": "https://petcarenote.com/#/features",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://petcarenote.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Features",
          "item": "https://petcarenote.com/#/features"
        }
      ]
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Pet Profiles",
          "description": "Create comprehensive digital profiles for all your pets"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Secure Sharing",
          "description": "Share pet information securely with caregivers and family"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Health Tracking",
          "description": "Track vaccinations, medications, and vet appointments"
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": "Schedule Management",
          "description": "Manage feeding times, medication schedules, and care routines"
        },
        {
          "@type": "ListItem",
          "position": 5,
          "name": "Mobile Friendly",
          "description": "Access your pet information anywhere, anytime"
        },
        {
          "@type": "ListItem",
          "position": 6,
          "name": "Multi-Language Support",
          "description": "Available in English, Chinese, and Japanese"
        }
      ]
    }
  };

  return (
    <>
      <SEO 
        title={t('common.seo.features.title')}
        description={t('common.seo.features.description')}
        keywords={t('common.seo.features.keywords')}
        structuredData={structuredData}
      />
      <div className="pt-20 sm:pt-24">
        {/* Hero Section */}
        <section
          ref={heroRef}
          className="bg-gradient-to-br from-primary-600 to-secondary-700 pt-16 pb-20 px-6 text-white relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-10"></div>
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7 }}
            >
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <SafeIcon icon={FiStar} className="text-3xl" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('features.hero.title')}</h1>
              <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto">
                {t('features.hero.subtitle')}
              </p>
            </motion.div>
          </div>

          {/* Mini Wave Divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 60" className="fill-white">
              <path d="M0,32L80,37.3C160,43,320,53,480,48C640,43,800,21,960,16C1120,11,1280,21,1360,26.7L1440,32L1440,60L1360,60C1280,60,1120,60,960,60C800,60,640,60,480,60C320,60,160,60,80,60L0,60Z"></path>
            </svg>
          </div>
        </section>

        {/* Core Features Section */}
        <section ref={coreRef} className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={coreInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-800 text-sm font-medium mb-4 shadow-sm">
                {t('features.core.badge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">
                {t('features.core.title')}
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-primary-600 to-secondary-600 mx-auto mb-6"></div>
              <p className="text-neutral-600 max-w-3xl mx-auto text-lg">
                {t('features.core.description')}
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              animate={coreInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FeatureCard
                icon={FiLayers}
                title={t('features.core.petProfiles.title')}
                description={t('features.core.petProfiles.description')}
                color="bg-primary-500"
                features={[
                  t('features.core.petProfiles.feature1'),
                  t('features.core.petProfiles.feature2'),
                  t('features.core.petProfiles.feature3'),
                  t('features.core.petProfiles.feature4')
                ]}
              />

              <FeatureCard
                icon={FiShare2}
                title={t('features.core.secureSharing.title')}
                description={t('features.core.secureSharing.description')}
                color="bg-secondary-500"
                features={[
                  t('features.core.secureSharing.feature1'),
                  t('features.core.secureSharing.feature2'),
                  t('features.core.secureSharing.feature3'),
                  t('features.core.secureSharing.feature4')
                ]}
              />

              <FeatureCard
                icon={FiCalendar}
                title={t('features.core.schedule.title')}
                description={t('features.core.schedule.description')}
                color="bg-accent-500"
                features={[
                  t('features.core.schedule.feature1'),
                  t('features.core.schedule.feature2'),
                  t('features.core.schedule.feature3'),
                  t('features.core.schedule.feature4')
                ]}
              />

              <FeatureCard
                icon={FiHeart}
                title={t('features.core.healthTracking.title')}
                description={t('features.core.healthTracking.description')}
                color="bg-danger-500"
                features={[
                  t('features.core.healthTracking.feature1'),
                  t('features.core.healthTracking.feature2'),
                  t('features.core.healthTracking.feature3'),
                  t('features.core.healthTracking.feature4')
                ]}
              />

              <FeatureCard
                icon={FiSmartphone}
                title={t('features.core.mobileFriendly.title')}
                description={t('features.core.mobileFriendly.description')}
                color="bg-warning-500"
                features={[
                  t('features.core.mobileFriendly.feature1'),
                  t('features.core.mobileFriendly.feature2'),
                  t('features.core.mobileFriendly.feature3'),
                  t('features.core.mobileFriendly.feature4')
                ]}
              />

              <FeatureCard
                icon={FiGlobe}
                title={t('features.core.multiLanguage.title')}
                description={t('features.core.multiLanguage.description')}
                color="bg-success-500"
                features={[
                  t('features.core.multiLanguage.feature1'),
                  t('features.core.multiLanguage.feature2'),
                  t('features.core.multiLanguage.feature3'),
                  t('features.core.multiLanguage.feature4')
                ]}
              />
            </motion.div>
          </div>
        </section>

        {/* Advanced Features Section */}
        <section
          ref={advancedRef}
          className="py-16 px-6 bg-gradient-to-br from-neutral-50 to-primary-50 relative overflow-hidden"
        >
          {/* Top Mini Wave */}
          <div className="absolute top-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 40" className="fill-white rotate-180">
              <path d="M0,20L80,22.7C160,25,320,31,480,28C640,25,800,15,960,12C1120,9,1280,15,1360,18L1440,20L1440,40L1360,40C1280,40,1120,40,960,40C800,40,640,40,480,40C320,40,160,40,80,40L0,40Z"></path>
            </svg>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={advancedInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-secondary-100 text-secondary-800 text-sm font-medium mb-4 shadow-sm">
                {t('features.advanced.badge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">
                {t('features.advanced.title')}
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-secondary-600 to-primary-600 mx-auto mb-6"></div>
              <p className="text-neutral-600 max-w-3xl mx-auto text-lg">
                {t('features.advanced.description')}
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              animate={advancedInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <AdvancedFeatureCard
                icon={FiLock}
                title={t('features.advanced.security.title')}
                description={t('features.advanced.security.description')}
                status="available"
              />

              <AdvancedFeatureCard
                icon={FiUsers}
                title={t('features.advanced.multiUser.title')}
                description={t('features.advanced.multiUser.description')}
                status="available"
              />

              <AdvancedFeatureCard
                icon={FiEdit}
                title={t('features.advanced.editing.title')}
                description={t('features.advanced.editing.description')}
                status="available"
              />

              <AdvancedFeatureCard
                icon={FiDatabase}
                title={t('features.advanced.cloudSync.title')}
                description={t('features.advanced.cloudSync.description')}
                status="available"
              />

              <AdvancedFeatureCard
                icon={FiMapPin}
                title={t('features.advanced.location.title')}
                description={t('features.advanced.location.description')}
                status="available"
              />

              <AdvancedFeatureCard
                icon={FiZap}
                title={t('features.advanced.performance.title')}
                description={t('features.advanced.performance.description')}
                status="available"
              />
            </motion.div>
          </div>

          {/* Bottom Mini Wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 40" className="fill-white">
              <path d="M0,20L80,22.7C160,25,320,31,480,28C640,25,800,15,960,12C1120,9,1280,15,1360,18L1440,20L1440,40L1360,40C1280,40,1120,40,960,40C800,40,640,40,480,40C320,40,160,40,80,40L0,40Z"></path>
            </svg>
          </div>
        </section>

        {/* Coming Soon Section */}
        <section ref={comingSoonRef} className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={comingSoonInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-800 text-sm font-medium mb-4 shadow-sm">
                {t('features.upcoming.badge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">
                {t('features.upcoming.title')}
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-accent-600 to-secondary-600 mx-auto mb-6"></div>
              <p className="text-neutral-600 max-w-3xl mx-auto text-lg">
                {t('features.upcoming.description')}
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              animate={comingSoonInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <UpcomingFeatureCard
                icon={FiBell}
                title={t('features.upcoming.notifications.title')}
                description={t('features.upcoming.notifications.description')}
                comingSoonText={t('features.upcoming.badge')}
              />

              <UpcomingFeatureCard
                icon={FiRefreshCw}
                title={t('features.upcoming.backup.title')}
                description={t('features.upcoming.backup.description')}
                comingSoonText={t('features.upcoming.badge')}
              />

              <UpcomingFeatureCard
                icon={FiCalendar}
                title={t('features.upcoming.calendar.title')}
                description={t('features.upcoming.calendar.description')}
                comingSoonText={t('features.upcoming.badge')}
              />

              <UpcomingFeatureCard
                icon={FiUsers}
                title={t('features.upcoming.community.title')}
                description={t('features.upcoming.community.description')}
                comingSoonText={t('features.upcoming.badge')}
              />

              <UpcomingFeatureCard
                icon={FiHeart}
                title={t('features.upcoming.healthTrends.title')}
                description={t('features.upcoming.healthTrends.description')}
                comingSoonText={t('features.upcoming.badge')}
              />

              <UpcomingFeatureCard
                icon={FiShield}
                title={t('features.upcoming.vetIntegration.title')}
                description={t('features.upcoming.vetIntegration.description')}
                comingSoonText={t('features.upcoming.badge')}
              />
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 bg-gradient-to-br from-primary-600 to-secondary-700 text-white relative overflow-hidden">
          {/* Top Mini Wave */}
          <div className="absolute top-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 40" className="fill-white rotate-180">
              <path d="M0,20L80,22.7C160,25,320,31,480,28C640,25,800,15,960,12C1120,9,1280,15,1360,18L1440,20L1440,40L1360,40C1280,40,1120,40,960,40C800,40,640,40,480,40C320,40,160,40,80,40L0,40Z"></path>
            </svg>
          </div>

          <div className="max-w-7xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('features.cta.title')}
              </h2>
              <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto">
                {t('features.cta.subtitle')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <LocalizedLink to="/dashboard" className="btn btn-accent btn-lg shadow-lg">
                  {t('common.actions.getStarted')}
                </LocalizedLink>
                <LocalizedLink to="/auth" className="btn btn-white btn-lg">
                  {t('auth.signUpButton')}
                </LocalizedLink>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

const FeatureCard = ({ icon, title, description, color, features }) => {
  return (
    <motion.div
      className="bg-white rounded-xl shadow-smooth hover:shadow-card transition-all duration-300 border border-neutral-100 overflow-hidden"
      whileHover={{ y: -5 }}
      transition={{ duration: 0.2 }}
    >
      <div className={`${color} text-white p-6 text-center`}>
        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={icon} className="text-3xl" />
        </div>
        <h3 className="text-xl font-semibold">{title}</h3>
      </div>
      <div className="p-6">
        <p className="text-neutral-600 mb-4">{description}</p>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center text-sm text-neutral-700">
              <SafeIcon icon={FiCheckCircle} className="text-success-500 mr-2 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

const AdvancedFeatureCard = ({ icon, title, description, status }) => {
  const { t } = useLanguage();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <span className="badge badge-success">{t('features.status.available')}</span>;
      case 'beta':
        return <span className="badge badge-warning">{t('features.status.beta')}</span>;
      default:
        return <span className="badge badge-primary">{status}</span>;
    }
  };

  return (
    <motion.div
      className="bg-white rounded-xl shadow-smooth hover:shadow-card transition-all duration-300 border border-neutral-100 p-6"
      whileHover={{ y: -5 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
          <SafeIcon icon={icon} className="text-xl text-primary-600" />
        </div>
        {getStatusBadge(status)}
      </div>
      <h3 className="text-xl font-semibold mb-3 text-neutral-800">{title}</h3>
      <p className="text-neutral-600">{description}</p>
    </motion.div>
  );
};

const UpcomingFeatureCard = ({ icon, title, description, comingSoonText }) => {
  return (
    <motion.div
      className="bg-white rounded-xl border-2 border-dashed border-neutral-300 p-6 text-center"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <SafeIcon icon={icon} className="text-2xl text-neutral-400" />
      </div>
      <h3 className="text-xl font-semibold mb-3 text-neutral-800">{title}</h3>
      <p className="text-neutral-600 mb-4">{description}</p>
      <div className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium">
        <SafeIcon icon={FiClock} className="mr-1" />
        {comingSoonText}
      </div>
    </motion.div>
  );
};

export default FeaturesPage;